import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Store, X } from "lucide-react";
import { toast } from "sonner";
import { api, unwrapEnvelope } from "@/lib/api";
import { formatDateTime, formatRelative } from "@/lib/settings/constants";
import { IntegrationStatusBadge } from "../IntegrationCard";
import { SYNC_OPTIONS } from "./walmartConnectReducer";
import { WalmartCredentialReplaceModal } from "./WalmartCredentialReplaceModal";
import { WalmartDisconnectConfirmModal } from "./WalmartDisconnectConfirmModal";

export function WalmartManageModal({
  integration,
  open,
  onClose,
  onUpdated,
}) {
  const [syncSettings, setSyncSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  useEffect(() => {
    if (integration?.sync_settings) {
      setSyncSettings({ ...integration.sync_settings });
    }
  }, [integration]);

  if (!open || !integration) return null;

  const capabilities = integration.capabilities || {};
  const visibleSync = SYNC_OPTIONS.filter((opt) => capabilities[opt.id]);

  async function saveSyncSettings() {
    setSaving(true);
    try {
      const res = await api.walmartUpdateSettings(syncSettings);
      const data = unwrapEnvelope(res);
      onUpdated?.(data?.integration);
      toast.success("Sync settings saved");
    } catch {
      toast.error("Could not save sync settings");
    } finally {
      setSaving(false);
    }
  }

  function toggleSync(id, value) {
    setSyncSettings((prev) => ({ ...prev, [id]: value }));
  }

  return (
    <>
      <Dialog.Root open={open} onOpenChange={(next) => !next && onClose?.()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[90]" />
          <Dialog.Content
            className="integration-modal fixed left-1/2 top-1/2 z-[91] w-[min(900px,calc(100vw-24px))] max-h-[90dvh] overflow-y-auto overflow-x-hidden -translate-x-1/2 -translate-y-1/2 bg-white rounded-panel border border-edge-subtle shadow-menu p-5"
            data-testid="walmart-manage-modal"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <Dialog.Title className="sr-only">Walmart Seller Central</Dialog.Title>
              <button
                type="button"
                className="p-1 rounded-ctrl hover:bg-surface-2 ml-auto"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <section className="walmart-manage-status flex items-start gap-3 mb-4">
              <div className="integration-card__icon">
                <Store className="h-4 w-4 text-ink-muted" />
              </div>
              <div>
                <strong className="text-stitle text-ink-primary block">
                  Walmart Seller Central
                </strong>
                <IntegrationStatusBadge status={integration.status} />
              </div>
            </section>

            <dl className="integration-details grid grid-cols-1 sm:grid-cols-2 gap-3 text-compact mb-6">
              <DetailRow
                label="Seller"
                value={integration.seller?.seller_name || "—"}
              />
              <DetailRow
                label="Seller ID"
                value={integration.seller?.seller_id || "—"}
              />
              <DetailRow
                label="Marketplace"
                value={integration.seller?.marketplace || "—"}
              />
              <DetailRow
                label="Account status"
                value={integration.seller?.account_status || "—"}
              />
              <DetailRow
                label="Connected"
                value={formatDateTime(integration.connected_at)}
              />
              <DetailRow
                label="Last verified"
                value={formatDateTime(integration.last_verified_at)}
              />
              <DetailRow
                label="Last sync"
                value={
                  integration.last_sync_at
                    ? formatRelative(integration.last_sync_at)
                    : "Not synced yet"
                }
              />
            </dl>

            {integration.client_id_masked && (
              <section className="mb-6 space-y-2 text-compact">
                <div>
                  <span className="text-ink-secondary">Client ID</span>
                  <div className="font-mono text-ink-primary mt-0.5">
                    {integration.client_id_masked}
                  </div>
                </div>
                <div>
                  <span className="text-ink-secondary">Client Secret</span>
                  <div className="font-mono text-ink-primary mt-0.5">••••••••••••</div>
                </div>
              </section>
            )}

            <section className="manage-sync-settings mb-6">
              <h3 className="text-stitle text-ink-primary mb-3">Sync settings</h3>
              <div className="space-y-2">
                {visibleSync.map((opt) => (
                  <label
                    key={opt.id}
                    className="flex items-center justify-between py-1 text-compact"
                    data-testid={`walmart-sync-${opt.id}`}
                  >
                    <span>{opt.title}</span>
                    <input
                      type="checkbox"
                      checked={!!syncSettings[opt.id]}
                      onChange={(e) => toggleSync(opt.id, e.target.checked)}
                    />
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="mt-3 h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact disabled:opacity-50"
                disabled={saving}
                onClick={saveSyncSettings}
                data-testid="walmart-save-sync"
              >
                {saving ? "Saving…" : "Save sync settings"}
              </button>
            </section>

            <footer className="integration-modal__footer flex flex-col sm:flex-row gap-2 pt-2">
              <button
                type="button"
                className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact"
                onClick={() => setReplaceOpen(true)}
                data-testid="walmart-update-credentials"
              >
                Update credentials
              </button>
              <button
                type="button"
                className="h-9 px-3 rounded-ctrl bg-red-600 text-white text-compact"
                onClick={() => setDisconnectOpen(true)}
                data-testid="walmart-disconnect"
              >
                Disconnect
              </button>
            </footer>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <WalmartCredentialReplaceModal
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        onReplaced={(next) => {
          onUpdated?.(next);
          setReplaceOpen(false);
        }}
      />

      <WalmartDisconnectConfirmModal
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        onDisconnected={() => {
          setDisconnectOpen(false);
          onClose?.();
          onUpdated?.(null);
        }}
      />
    </>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <dt className="text-micro text-ink-muted uppercase">{label}</dt>
      <dd className="text-ink-primary mt-0.5">{value}</dd>
    </div>
  );
}
